<script setup lang="ts">
/**
 * 阶段计划改期的原因弹窗（0926 Tony：阶段计划改日期必须带原因）。
 *
 * 和派活的 RescheduleDialog 长得一样，但这里不发请求：日期已经在外面选好了，
 * 这个框只收「分类 + 一句话」，交回给调用方一起 PATCH。
 * 批量（顺延、AI 排期、多选）问一次，count 写几条。
 */
import { computed, ref } from 'vue';

import { DELAY_CATEGORIES, type DelayCategory } from '@/types/tasks';
import { dayDiff } from '@/utils/plan-node-reschedule';

const props = withDefaults(defineProps<{
  title: string;
  fromDay: string | null;
  toDay: string | null;
  count?: number;
}>(), { count: 1 });
const emit = defineEmits<{ confirm: [answer: { category: DelayCategory; reason: string }]; cancel: [] }>();

const category = ref<DelayCategory>('其他');
const reason = ref('');
const error = ref('');

const slipDays = computed(() => (props.fromDay && props.toDay ? dayDiff(props.fromDay, props.toDay) : null));
const change = computed(() => {
  // 批量（顺延、AI 排期、Excel、多选）问一次：各条日期不同，只说有几条
  if (props.count > 1 && (!props.fromDay || !props.toDay)) {
    return `这批里有 ${props.count} 条已排期的活动要改计划完成日期，原因一起写`;
  }
  if (!props.toDay) return `原定 ${props.fromDay ?? '—'}，现在要清空`;
  const slip = slipDays.value ?? 0;
  return `原定 ${props.fromDay ?? '—'} → ${props.toDay}${slip > 0 ? `，延后 ${slip} 天` : slip < 0 ? `，提前 ${-slip} 天` : ''}`;
});

function submit(): void {
  if (!reason.value.trim()) {
    error.value = '写一句为什么改——不限长短，但不能空着。';
    return;
  }
  emit('confirm', { category: category.value, reason: reason.value.trim() });
}
</script>

<template>
  <div class="dialog-mask" role="dialog" aria-modal="true" aria-label="改期原因" @click.self="emit('cancel')">
    <section class="dialog-card node-reschedule">
      <header>
        <span>RESCHEDULE</span>
        <h2>改计划日期要写原因</h2>
        <p>{{ title }}<template v-if="count > 1"> 等 {{ count }} 条</template></p>
        <p class="meta">{{ change }}</p>
      </header>
      <label>
        <span>原因分类<i>*</i></span>
        <select v-model="category" aria-label="原因分类">
          <option v-for="entry in DELAY_CATEGORIES" :key="entry" :value="entry">{{ entry }}</option>
        </select>
      </label>
      <label>
        <span>为什么改<i>*</i></span>
        <textarea
          v-model="reason" rows="3" maxlength="500" aria-label="为什么改"
          placeholder="一句话说清楚：卡在哪、什么时候能解、新日期怎么定的"
          @input="error = ''"
        ></textarea>
        <small v-if="error" class="err">{{ error }}</small>
      </label>
      <p class="hint">责任人自己改的算本人延期；项目经理改的算排期调整，不记到责任人头上。原因会进延期统计和这条活动的进展留痕。</p>
      <footer>
        <button class="button button-secondary" type="button" @click="emit('cancel')">取消，不改了</button>
        <button class="button button-primary" type="button" @click="submit">确认改期</button>
      </footer>
    </section>
  </div>
</template>

<style scoped>
.dialog-mask {
  position: fixed; inset: 0; z-index: 70; display: grid; place-items: center;
  background: rgba(15, 43, 82, .32); padding: 20px;
}
.dialog-card {
  width: min(520px, 100%); display: grid; gap: 14px; padding: 22px 24px 20px;
  background: var(--white); border: 1px solid var(--line); border-radius: 14px;
  box-shadow: 0 24px 60px rgba(15, 43, 82, .22);
}
.dialog-card header span { color: var(--blue); font: 800 12px var(--display); letter-spacing: .12em; }
.dialog-card header h2 { margin: 2px 0 6px; }
.dialog-card header p { margin: 0; color: var(--ink); font-size: 13px; line-height: 1.6; font-weight: 700; }
.dialog-card header p.meta { color: var(--risk); font-weight: 700; }
.dialog-card label { display: grid; gap: 6px; font-size: 13px; font-weight: 700; }
.dialog-card label i { color: var(--risk); font-style: normal; }
.dialog-card label small.err { color: var(--risk); font-weight: 700; font-size: 12px; }
.hint { margin: 0; color: var(--muted); font-size: 12px; line-height: 1.6; }
.dialog-card footer { display: flex; justify-content: flex-end; gap: 10px; margin-top: 4px; }
</style>
